import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { useCollection } from "@sciencecorp/helix-components";
import { z } from "zod";
import { api, isApiError } from "..";
import { zodParse } from "../zodParse";

type CollectionFilters = ReturnType<typeof useCollection>["filters"];

// Schema for admin application list item - minimal fields only
const AdminApplicationSchema = z.object({
  id: z.string().uuid(),
  is_submitted: z.boolean(),
  submitted_at: z.string().datetime().nullable(),
  status: z.enum(["pending", "approved", "rejected"]),
  applicant_type: z.enum(["self", "proxy"]).nullable(),
  patient_country: z.string().nullable(),
  patient_date_of_birth: z.string().nullable(),
  vision_acuity_known: z.boolean().nullable(),
  vision_acuity_left: z.string().nullable(),
  vision_acuity_right: z.string().nullable(),
  vision_can_read_normal_print: z.boolean().nullable(),
  vision_can_read_headlines: z.boolean().nullable(),
  vision_can_see_indoors: z.boolean().nullable(),
  vision_can_localize_person: z.boolean().nullable(),
  vision_conditions: z.array(z.string()),
  vision_macular_degeneration_type: z.string().nullable(),
  proxy_country: z.string().nullable(),
});

// Schema for application review
const ApplicationReviewSchema = z.object({
  id: z.string().uuid(),
  potential_candidate: z.boolean(),
  clinical_application: z.string(),
  created_at: z.string().datetime(),
  reviewer: z.object({
    id: z.string().uuid(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
  }),
});

// Schema for admin application detail view - includes additional fields needed for detail page
const AdminApplicationDetailSchema = AdminApplicationSchema.extend({
  additional_info: z.string().nullable(),
  personal_travel_willing: z.boolean().nullable(),
  patient_state: z.string().nullable(),
  vision_other_details: z.string().nullable(),
  vision_driving: z.string().nullable(),
  consent_locked: z.boolean(),
  consent_health_collection: z.boolean().nullable(),
  consent_health_sharing: z.boolean().nullable(),
  consent_contact: z.boolean().nullable(),
  consent_contact_email: z.boolean().nullable(),
  consent_contact_phone: z.boolean().nullable(),
  consent_contact_recurring: z.boolean().nullable(),
  proxy_state: z.string().nullable(),
  proxy_date_of_birth: z.string().nullable(),
  personal_country_of_residence: z.string().nullable(),
  applicant_is_adult: z.boolean().nullable(),
  reviews: z.array(ApplicationReviewSchema),
});

const AdminStatsSchema = z.object({
  total_accounts: z.number(),
  total_applications: z.number(),
  pending: z.number(),
  approved: z.number(),
  rejected: z.number(),
});

const PaginationMetaSchema = z.object({
  page: z.number(),
  per_page: z.number(),
  n_pages: z.number(),
  total: z.number(),
});

const FacetCountMap = z.record(z.string(), z.number());
const FacetCountsSchema = z.object({
  status: FacetCountMap,
  condition: FacetCountMap,
  amd_type: FacetCountMap,
  can_read_headlines: FacetCountMap,
  acuity_meets_prima: FacetCountMap,
  country: FacetCountMap,
  travel_willing: FacetCountMap,
});

const AdminApplicationsResponseSchema = z.object({
  applications: z.array(AdminApplicationSchema),
  stats: AdminStatsSchema,
  pagination: PaginationMetaSchema,
  facet_counts: FacetCountsSchema,
});

export type AdminApplication = z.infer<typeof AdminApplicationSchema>;
export type AdminApplicationDetail = z.infer<typeof AdminApplicationDetailSchema>;
export type AdminStats = z.infer<typeof AdminStatsSchema>;
export type AdminApplicationsResponse = z.infer<typeof AdminApplicationsResponseSchema>;
export type AdminFacetCounts = z.infer<typeof FacetCountsSchema>;
export type ApplicationReview = z.infer<typeof ApplicationReviewSchema>;

export interface AdminApplicationsParams {
  filters?: CollectionFilters;
  page?: number;
  per_page?: number;
  order?: Record<string, "asc" | "desc">;
}

const buildQueryParams = ({
  filters,
  page,
  per_page,
  order,
}: AdminApplicationsParams): Record<string, unknown> => {
  const query: Record<string, unknown> = {};
  if (filters) {
    for (const [key, values] of Object.entries(filters)) {
      if (Array.isArray(values) && values.length > 0) query[key] = values;
    }
  }
  if (page) query.page = page;
  if (per_page) query.per_page = per_page;
  if (order) {
    const key = Object.keys(order)[0];
    if (key) {
      query.order_key = key;
      query.order_direction = order[key];
    }
  }
  return query;
};

async function fetchAdminApplications(
  params: AdminApplicationsParams,
): Promise<AdminApplicationsResponse> {
  try {
    const { data } = await api.get("/admin/applications", {
      params: buildQueryParams(params),
    });
    return zodParse(AdminApplicationsResponseSchema, data);
  } catch (error) {
    if (isApiError(error)) {
      if (error.response?.status === 403) {
        throw new Error("Access denied. Admin privileges required.");
      }
      throw new Error(error.response?.data?.message || "Failed to fetch applications");
    }
    throw error;
  }
}

export function useAdminApplications(params: AdminApplicationsParams = {}) {
  return useQuery({
    queryKey: ["admin", "applications", params],
    queryFn: () => fetchAdminApplications(params),
    keepPreviousData: true,
  });
}

async function fetchAdminApplication(id: string): Promise<AdminApplicationDetail> {
  try {
    const { data } = await api.get(`/admin/applications/${id}`);
    return zodParse(AdminApplicationDetailSchema, data);
  } catch (error) {
    if (isApiError(error)) {
      if (error.response?.status === 403) {
        throw new Error("Access denied. Admin privileges required.");
      }
      if (error.response?.status === 404) {
        throw new Error("Application not found.");
      }
      throw new Error(error.response?.data?.message || "Failed to fetch application");
    }
    throw error;
  }
}

export function useAdminApplication(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "application", id],
    queryFn: () => fetchAdminApplication(id!),
    enabled: !!id,
  });
}

// Create a review for an application
async function createReview(
  applicationId: string,
  potentialCandidate: boolean,
): Promise<ApplicationReview> {
  try {
    const { data } = await api.post(`/admin/applications/${applicationId}/reviews`, {
      review: { potential_candidate: potentialCandidate },
    });
    return zodParse(ApplicationReviewSchema, data);
  } catch (error) {
    if (isApiError(error)) {
      if (error.response?.status === 403) {
        throw new Error("Access denied. Reviewer privileges required.");
      }
      throw new Error(error.response?.data?.errors?.join(", ") || "Failed to create review");
    }
    throw error;
  }
}

export function useCreateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      applicationId,
      potentialCandidate,
    }: {
      applicationId: string;
      potentialCandidate: boolean;
    }) => createReview(applicationId, potentialCandidate),
    onSuccess: (_, variables) => {
      // Invalidate the application query to refetch with new review
      queryClient.invalidateQueries({
        queryKey: ["admin", "application", variables.applicationId],
      });
    },
  });
}
